/**
 * Policy Evaluator V2 (shadow + governance + session + candidate + proposal cutover)
 *
 * - Governance: GOVERNANCE_ACTIONS → engine "v2-legitimacy".
 * - Session: SESSION_ACTIONS → engine "v2-session".
 * - Candidate modification: CANDIDATE_MODIFICATION_ACTIONS → engine "v2-candidate-mod".
 * - Proposal configuration: PROPOSAL_CONFIGURATION_ACTIONS → engine "v2-proposal-config".
 * - Shadow: all other actions → engine "v2-shadow-delegate".
 */

import type {
  ActorContext,
  ActionContext,
  ResourceContext,
  EvaluationContext,
  PolicyDecision,
  PolicyRule,
  CapabilityChecker,
  ExplainResult,
} from './types';
import { hasAuthoredPolicyRules } from './authoredPolicies';
// ============================================================================
// Governance Actions (v2 cutover surface)
// ============================================================================

/**
 * Actions that affect legitimacy, overrides, or publish/ratify/decide.
 * Enforced via v2; engine "v2-legitimacy".
 */
export const GOVERNANCE_ACTIONS: ReadonlySet<string> = new Set([
  'lists.publish',
  'process.publish',
  'session.publish',
  'values.override',
]);

export function isGovernanceAction(actionKey: string): boolean {
  return GOVERNANCE_ACTIONS.has(actionKey);
}

// ============================================================================
// Session / Intent Actions (v2 cutover surface)
// ============================================================================

/**
 * Session create/run/operate and intent operate (criteria/value selection).
 * Enforced via v2; engine "v2-session". Do not include governance (e.g. values.override).
 */
export const SESSION_ACTIONS: ReadonlySet<string> = new Set([
  'session.view',
  'session.facilitate',
  'session.record',
  'session.participate',
  'session.vote',
  'values.propose',
  'values.accept',
  'values.reject',
  'criteria.select',
  'criteria.weight',
]);

export function isSessionAction(actionKey: string): boolean {
  return SESSION_ACTIONS.has(actionKey);
}

// ============================================================================
// Candidate Modification Authority (v2 cutover surface)
// ============================================================================

/**
 * Actions that modify candidate items or their placement: editing candidate
 * attributes/content, inclusion/exclusion, ranking/reordering, pool membership.
 * Enforced via v2; engine "v2-candidate-mod".
 */
export const CANDIDATE_MODIFICATION_ACTIONS: ReadonlySet<string> = new Set([
  // Editing candidate attributes and content
  'items.view',
  'items.create',
  'items.edit',
  'items.edit.content',
  'items.edit.status',
  'items.delete',
  // Ranking/reordering: cut line and prioritization participation
  'lists.move_cutline',
  'process.prioritize',
]);

export function isCandidateModificationAction(actionKey: string): boolean {
  return CANDIDATE_MODIFICATION_ACTIONS.has(actionKey);
}

// ============================================================================
// Proposal Configuration Authority (v2 cutover surface)
// ============================================================================

/**
 * Actions that configure the proposal container: criteria CRUD (not select/weight,
 * which are session intent), process/list structure, lock/unlock, attributes.
 * Enforced via v2; engine "v2-proposal-config".
 */
export const PROPOSAL_CONFIGURATION_ACTIONS: ReadonlySet<string> = new Set([
  // Criteria definition and management (selection/weighting are in SESSION_ACTIONS)
  'criteria.view',
  'criteria.create',
  'criteria.edit',
  'criteria.delete',
  // Process/cycle configuration
  'process.view',
  'process.create',
  'process.edit',
  // List/pool configuration
  'lists.view',
  'lists.create',
  'lists.edit',
  // Attribute definition and ownership (proposal container schema)
  'attributes.define',
  'attributes.assign_owner',
  // Pool membership (change proposal/session container composition)
  'cycle.pool.add',
  'cycle.pool.remove',
  'session.pool.add',
  'session.pool.remove',
]);

export function isProposalConfigurationAction(actionKey: string): boolean {
  return PROPOSAL_CONFIGURATION_ACTIONS.has(actionKey);
}

// ============================================================================
// Types
// ============================================================================

/** Input for v2 evaluation (same shape as v1 arguments). */
export interface AuthorizationEvaluationInput {
  actor: ActorContext;
  action: ActionContext;
  resource: ResourceContext;
  context: EvaluationContext;
  rules: PolicyRule[];
  capabilityChecker: CapabilityChecker;
}

/** Delegates to v1 evaluator (injected for shadow mode and tests). */
export interface AuthorizationV1Delegates {
  evaluateAuthorizationV1: (
    actor: ActorContext,
    action: ActionContext,
    resource: ResourceContext,
    context: EvaluationContext,
    rules: PolicyRule[],
    capabilityChecker: CapabilityChecker
  ) => PolicyDecision;
  evaluateAndExplainV1: (
    actor: ActorContext,
    action: ActionContext,
    resource: ResourceContext,
    context: EvaluationContext,
    rules: PolicyRule[],
    capabilityChecker: CapabilityChecker
  ) => { decision: PolicyDecision; explanation: ExplainResult };
}

/** V2 decision: same as PolicyDecision with optional engine metadata. */
export type AuthorizationDecision = PolicyDecision & { engine?: string };

/** V2 explanation: same as ExplainResult with optional engine metadata. */
export type AuthorizationExplanation = ExplainResult & { engine?: string };

const V2_ENGINE_SHADOW = 'v2-shadow-delegate';
const V2_ENGINE_LEGITIMACY = 'v2-legitimacy';
const V2_ENGINE_SESSION = 'v2-session';
const V2_ENGINE_CANDIDATE_MOD = 'v2-candidate-mod';
const V2_ENGINE_PROPOSAL_CONFIG = 'v2-proposal-config';

/** Canonical prerequisite codes from registry (eligibility for session intents). */
const PREREQ_CRITERIA_LOCKED = 'CRITERIA_LOCKED';
const PREREQ_POOL_LOCKED = 'POOL_LOCKED';
const PREREQ_VALUES_SUFFICIENT = 'VALUES_SUFFICIENT';
const PREREQ_VALUES_CONVERGED = 'VALUES_CONVERGED';
const PREREQ_CYCLE_POOL_LOCKED = 'CYCLE_POOL_LOCKED';
const PREREQ_SESSION_POOL_LOCKED = 'SESSION_POOL_LOCKED';
const PREREQ_ROUND_PROPOSAL_LOCKED = 'ROUND_PROPOSAL_LOCKED';
const PREREQ_DECISION_LOOPS_INCOMPLETE = 'DECISION_LOOPS_INCOMPLETE';

function getEngineForAction(actionKey: string): string {
  if (isGovernanceAction(actionKey)) return V2_ENGINE_LEGITIMACY;
  if (isSessionAction(actionKey)) return V2_ENGINE_SESSION;
  if (isCandidateModificationAction(actionKey)) return V2_ENGINE_CANDIDATE_MOD;
  if (isProposalConfigurationAction(actionKey)) return V2_ENGINE_PROPOSAL_CONFIG;
  return V2_ENGINE_SHADOW;
}

// ============================================================================
// values.accept: v2-native prerequisite enforcement (no delegation)
// ============================================================================

/**
 * Prereq check adapter for values.accept.
 * Uses context.prerequisites when present; maps unmet prereqs to canonical codes.
 * Does not load YAML; uses existing context only.
 * Exported for tests that assert computed unmet codes (e.g. three-lock scaffolding).
 */
export function evaluateValuesAcceptPrereqs(context: EvaluationContext): string[] {
  const unmet: string[] = [];
  const p = context.prerequisites;
  if (p == null) return unmet;
  if (p.criteriaLocked === false) unmet.push(PREREQ_CRITERIA_LOCKED);
  if (p.poolLocked === false) unmet.push(PREREQ_POOL_LOCKED);
  if (p.valuesSufficient === false) unmet.push(PREREQ_VALUES_SUFFICIENT);
  if (p.valuesConverged === false) unmet.push(PREREQ_VALUES_CONVERGED);
  if (p.cyclePoolLocked === false) unmet.push(PREREQ_CYCLE_POOL_LOCKED);
  if (p.sessionPoolLocked === false) unmet.push(PREREQ_SESSION_POOL_LOCKED);
  if (p.roundProposalLocked === false) unmet.push(PREREQ_ROUND_PROPOSAL_LOCKED);
  return unmet;
}

/**
 * Required prerequisite codes for values.accept (from registry / value_entry).
 * When context.prerequisites supplies these and they are false, we deny with the code.
 */
const VALUES_ACCEPT_REQUIRED_PREREQS = [
  PREREQ_CRITERIA_LOCKED,
  PREREQ_VALUES_SUFFICIENT,
] as const;

function evaluateValuesAccept(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker
): AuthorizationDecision {
  const { actor, action, resource, context } = input;
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-values-accept-capability',
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine: V2_ENGINE_SESSION,
    };
  }
  const unmet = evaluateValuesAcceptPrereqs(context);
  const requiredUnmet = unmet.filter((c) =>
    (VALUES_ACCEPT_REQUIRED_PREREQS as readonly string[]).includes(c)
  );
  if (requiredUnmet.length > 0) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-values-accept-prereqs',
      reasons: requiredUnmet.map((code) => ({ code, data: { prerequisite: code } })),
      capabilityPassed: true,
      engine: V2_ENGINE_SESSION,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: 'v2-values-accept-allowed',
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine: V2_ENGINE_SESSION,
  };
}

// ============================================================================
// criteria.edit: v2-native prerequisite enforcement (no delegation)
// ============================================================================

function evaluateCriteriaEdit(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker
): AuthorizationDecision {
  const { action, resource, context } = input;
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-criteria-edit-capability',
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  if (context.prerequisites?.criteriaLocked === false) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-criteria-edit-prereqs',
      reasons: [{ code: PREREQ_CRITERIA_LOCKED, data: { prerequisite: PREREQ_CRITERIA_LOCKED } }],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: 'v2-criteria-edit-allowed',
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine: V2_ENGINE_PROPOSAL_CONFIG,
  };
}

// ============================================================================
// criteria.create / criteria.delete: v2-native prerequisite enforcement (no delegation)
// ============================================================================

function evaluateCriteriaCreate(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker
): AuthorizationDecision {
  const { action, resource, context } = input;
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-criteria-create-capability',
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  if (context.prerequisites?.criteriaLocked === false) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-criteria-create-prereqs',
      reasons: [{ code: PREREQ_CRITERIA_LOCKED, data: { prerequisite: PREREQ_CRITERIA_LOCKED } }],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: 'v2-criteria-create-allowed',
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine: V2_ENGINE_PROPOSAL_CONFIG,
  };
}

function evaluateCriteriaDelete(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker
): AuthorizationDecision {
  const { action, resource, context } = input;
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-criteria-delete-capability',
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  if (context.prerequisites?.criteriaLocked === false) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-criteria-delete-prereqs',
      reasons: [{ code: PREREQ_CRITERIA_LOCKED, data: { prerequisite: PREREQ_CRITERIA_LOCKED } }],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: 'v2-criteria-delete-allowed',
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine: V2_ENGINE_PROPOSAL_CONFIG,
  };
}

// ============================================================================
// session.pool.add / session.pool.remove: v2-native SESSION_POOL_LOCKED (feature-flagged)
// ============================================================================

function isSessionPoolPrereqEnforceEnabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_SESSION_POOL_PREREQ === '1';
}

function isCyclePoolPrereqEnforceEnabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_CYCLE_POOL_PREREQ === '1';
}

function isRoundProposalPrereqEnforceEnabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_ROUND_PROPOSAL_PREREQ === '1';
}

/**
 * When ON, items.* write actions are evaluated v2-native (no v1 delegation).
 * Default OFF: unset => delegate to v1, engine "v2-candidate-mod".
 */
export function isItemsWriteV2Enabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_ITEMS_WRITE === '1';
}

/**
 * When ON, lists.move_cutline is evaluated v2-native (no v1 delegation).
 * Default OFF: unset => delegate to v1, engine "v2-candidate-mod".
 */
export function isListsMoveCutlineV2Enabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_LISTS_MOVE_CUTLINE === '1';
}

/**
 * When ON, process.edit and process.prioritize are evaluated v2-native (no v1 delegation).
 * Default OFF: unset => delegate to v1.
 */
export function isProcessWriteV2Enabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_PROCESS_WRITE === '1';
}

/** process.write action keys for v2-native (slice 3). */
const PROCESS_WRITE_ACTIONS: ReadonlySet<string> = new Set(['process.edit', 'process.prioritize']);

function getProcessWriteRuleIdSuffix(actionKey: string): string {
  if (actionKey === 'process.edit') return 'edit';
  if (actionKey === 'process.prioritize') return 'prioritize';
  return 'edit';
}

/**
 * V2-native evaluation for process.edit and process.prioritize.
 * process.prioritize (force_rank intent): also checks decisionLoopsIncomplete prerequisite.
 */
function evaluateProcessWriteAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: string
): AuthorizationDecision {
  const { action, resource, context } = input;
  const suffix = getProcessWriteRuleIdSuffix(actionKey);
  const engine = getEngineForAction(actionKey);
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: `v2-process-${suffix}-capability`,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine,
    };
  }
  if (actionKey === 'process.prioritize' && context.prerequisites?.decisionLoopsIncomplete === true) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-process-prioritize-prereqs',
      reasons: [
        {
          code: PREREQ_DECISION_LOOPS_INCOMPLETE,
          data: { prerequisite: PREREQ_DECISION_LOOPS_INCOMPLETE },
        },
      ],
      capabilityPassed: true,
      engine,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: `v2-process-${suffix}-allowed`,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine,
  };
}

// ============================================================================
// Slice 4: session.record + session.vote v2-native (flagged, capability-only)
// ============================================================================

export function isSessionWriteV2Enabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_SESSION_WRITE === '1';
}

const SESSION_WRITE_ACTIONS: ReadonlySet<string> = new Set(['session.record', 'session.vote']);

function getSessionWriteRuleIdSuffix(actionKey: string): string {
  if (actionKey === 'session.record') return 'record';
  if (actionKey === 'session.vote') return 'vote';
  return 'record';
}

function evaluateSessionWriteAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: string
): AuthorizationDecision {
  const { action, resource } = input;
  const suffix = getSessionWriteRuleIdSuffix(actionKey);
  const engine = getEngineForAction(actionKey);
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: `v2-session-${suffix}-capability`,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: `v2-session-${suffix}-allowed`,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine,
  };
}

// ============================================================================
// Slice 5: values.propose, values.reject, values.override v2-native (flagged, capability-only)
// ============================================================================

export function isValuesWriteV2Enabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_VALUES_WRITE === '1';
}

const VALUES_WRITE_ACTIONS: ReadonlySet<string> = new Set(['values.propose', 'values.reject', 'values.override']);

function getValuesWriteRuleIdSuffix(actionKey: string): string {
  if (actionKey === 'values.propose') return 'propose';
  if (actionKey === 'values.reject') return 'reject';
  if (actionKey === 'values.override') return 'override';
  return 'propose';
}

function evaluateValuesWriteAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: string
): AuthorizationDecision {
  const { action, resource } = input;
  const suffix = getValuesWriteRuleIdSuffix(actionKey);
  const engine = getEngineForAction(actionKey);
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: `v2-values-${suffix}-capability`,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: `v2-values-${suffix}-allowed`,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine,
  };
}

// ============================================================================
// Slice 6: lists.publish + process.publish v2-native (flagged, capability-only)
// ============================================================================

export function isPublishWriteV2Enabled(): boolean {
  return typeof process !== 'undefined' && process.env?.AUTHZ_V2_PUBLISH_WRITE === '1';
}

const PUBLISH_WRITE_ACTIONS: ReadonlySet<string> = new Set(['lists.publish', 'process.publish', 'session.publish']);

function getPublishWriteRuleIdSuffix(actionKey: string): string {
  if (actionKey === 'lists.publish') return 'lists-publish';
  if (actionKey === 'process.publish') return 'process-publish';
  if (actionKey === 'session.publish') return 'session-publish';
  return 'lists-publish';
}

function evaluatePublishWriteAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: string
): AuthorizationDecision {
  const { action, resource } = input;
  const suffix = getPublishWriteRuleIdSuffix(actionKey);
  const engine = getEngineForAction(actionKey);
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: `v2-publish-${suffix}-capability`,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: `v2-publish-${suffix}-allowed`,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine,
  };
}

/**
 * V2-native evaluation for lists.move_cutline (capability-only; no prereqs today).
 * Resource type: list; capability key: lists.move_cutline.
 */
function evaluateListsMoveCutlineAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker
): AuthorizationDecision {
  const { action, resource } = input;
  const engine = getEngineForAction('lists.move_cutline');
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: 'v2-lists-move-cutline-capability',
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: 'v2-lists-move-cutline-allowed',
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine,
  };
}

/** items.* write action keys that can be made v2-native (slice 1). */
const ITEMS_WRITE_ACTIONS: ReadonlySet<string> = new Set([
  'items.create',
  'items.edit',
  'items.edit.content',
  'items.edit.status',
  'items.delete',
]);

function getItemsWriteRuleIdSuffix(actionKey: string): string {
  if (actionKey === 'items.create') return 'create';
  if (actionKey === 'items.edit') return 'edit';
  if (actionKey === 'items.edit.content') return 'edit-content';
  if (actionKey === 'items.edit.status') return 'edit-status';
  if (actionKey === 'items.delete') return 'delete';
  if (actionKey === 'items.edit') return 'edit';
  return 'edit';
}

/**
 * V2-native evaluation for items.* write actions (capability-only; no prereqs today).
 * Used when AUTHZ_V2_ITEMS_WRITE=1.
 */
function evaluateItemsWriteAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: string,
  delegates: AuthorizationV1Delegates
): AuthorizationDecision {
  const { actor, action, resource, context, rules } = input;
  const suffix = getItemsWriteRuleIdSuffix(actionKey);
  const engine = getEngineForAction(actionKey);
  if (hasAuthoredPolicyRules(rules)) {
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine };
  }
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: `v2-items-${suffix}-capability`,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: `v2-items-${suffix}-allowed`,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine,
  };
}

/** Minimal action set for ROUND_PROPOSAL_LOCKED gating (feature-flagged). */
const ROUND_PROPOSAL_CRITERIA_ACTIONS = new Set<string>([
  'criteria.edit',
  'criteria.create',
  'criteria.delete',
]);

function getRoundProposalRuleIdSuffix(actionKey: string): string {
  if (actionKey === 'criteria.edit') return 'criteria-edit';
  if (actionKey === 'criteria.create') return 'criteria-create';
  if (actionKey === 'criteria.delete') return 'criteria-delete';
  return 'criteria-edit';
}

function evaluateRoundProposalAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: 'criteria.edit' | 'criteria.create' | 'criteria.delete'
): AuthorizationDecision {
  const { action, resource, context } = input;
  const suffix = getRoundProposalRuleIdSuffix(actionKey);
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: `v2-round-proposal-${suffix}-capability`,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  const p = context.prerequisites;
  if (p == null || p.roundProposalLocked === undefined) {
    return {
      allowed: true,
      decision: 'allow',
      ruleId: `v2-round-proposal-${suffix}-allowed`,
      reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  if (p.roundProposalLocked === false) {
    return {
      allowed: false,
      decision: 'deny',
      ruleId: `v2-round-proposal-${suffix}-prereqs`,
      reasons: [
        {
          code: PREREQ_ROUND_PROPOSAL_LOCKED,
          data: { prerequisite: PREREQ_ROUND_PROPOSAL_LOCKED },
        },
      ],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  return {
    allowed: true,
    decision: 'allow',
    ruleId: `v2-round-proposal-${suffix}-allowed`,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine: V2_ENGINE_PROPOSAL_CONFIG,
  };
}

function evaluateSessionPoolAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: 'session.pool.add' | 'session.pool.remove'
): AuthorizationDecision {
  const { action, resource, context, actor } = input;
  let hasCapability = capabilityChecker(action.key, resource.scope);
  /**
   * Session creator or designated facilitator may add/remove session-specific pool items without
   * POOL_SELECT / session.pool.add in their role bundle (common for app-linked and track-linked sessions).
   * Row assignment (created_by / facilitator_id) is the source of truth for who configures the pool.
   */
  if (!hasCapability && actor?.userId) {
    const attrs = resource.attributes;
    const facilitatorRaw =
      attrs && typeof attrs === 'object' && attrs !== null && 'facilitatorId' in attrs
        ? (attrs as Record<string, unknown>).facilitatorId
        : undefined;
    const facilitatorId = facilitatorRaw != null && facilitatorRaw !== '' ? String(facilitatorRaw) : '';
    const isSessionLead =
      (!!resource.createdBy && actor.userId === resource.createdBy) ||
      (!!facilitatorId && actor.userId === facilitatorId);
    if (isSessionLead) {
      hasCapability = true;
    }
  }
  if (!hasCapability) {
    const ruleId =
      actionKey === 'session.pool.add' ? 'v2-session-pool-add-capability' : 'v2-session-pool-remove-capability';
    return {
      allowed: false,
      decision: 'deny',
      ruleId,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  const p = context.prerequisites;
  if (p == null || p.sessionPoolLocked === undefined) {
    const ruleId =
      actionKey === 'session.pool.add' ? 'v2-session-pool-add-allowed' : 'v2-session-pool-remove-allowed';
    return {
      allowed: true,
      decision: 'allow',
      ruleId,
      reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  if (p.sessionPoolLocked === false) {
    const ruleId =
      actionKey === 'session.pool.add' ? 'v2-session-pool-add-prereqs' : 'v2-session-pool-remove-prereqs';
    return {
      allowed: false,
      decision: 'deny',
      ruleId,
      reasons: [
        {
          code: PREREQ_SESSION_POOL_LOCKED,
          data: { prerequisite: PREREQ_SESSION_POOL_LOCKED },
        },
      ],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  const ruleId =
    actionKey === 'session.pool.add' ? 'v2-session-pool-add-allowed' : 'v2-session-pool-remove-allowed';
  return {
    allowed: true,
    decision: 'allow',
    ruleId,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine: V2_ENGINE_PROPOSAL_CONFIG,
  };
}

// ============================================================================
// cycle.pool.add / cycle.pool.remove: v2-native CYCLE_POOL_LOCKED (feature-flagged)
// ============================================================================

function evaluateCyclePoolAction(
  input: AuthorizationEvaluationInput,
  capabilityChecker: CapabilityChecker,
  actionKey: 'cycle.pool.add' | 'cycle.pool.remove'
): AuthorizationDecision {
  const { action, resource, context } = input;
  const hasCapability = capabilityChecker(action.key, resource.scope);
  if (!hasCapability) {
    const ruleId =
      actionKey === 'cycle.pool.add' ? 'v2-cycle-pool-add-capability' : 'v2-cycle-pool-remove-capability';
    return {
      allowed: false,
      decision: 'deny',
      ruleId,
      reasons: [{ code: 'MISSING_CAPABILITY', data: { permission: action.key } }],
      capabilityPassed: false,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  const p = context.prerequisites;
  if (p == null || p.cyclePoolLocked === undefined) {
    const ruleId =
      actionKey === 'cycle.pool.add' ? 'v2-cycle-pool-add-allowed' : 'v2-cycle-pool-remove-allowed';
    return {
      allowed: true,
      decision: 'allow',
      ruleId,
      reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  if (p.cyclePoolLocked === false) {
    const ruleId =
      actionKey === 'cycle.pool.add' ? 'v2-cycle-pool-add-prereqs' : 'v2-cycle-pool-remove-prereqs';
    return {
      allowed: false,
      decision: 'deny',
      ruleId,
      reasons: [
        {
          code: PREREQ_CYCLE_POOL_LOCKED,
          data: { prerequisite: PREREQ_CYCLE_POOL_LOCKED },
        },
      ],
      capabilityPassed: true,
      engine: V2_ENGINE_PROPOSAL_CONFIG,
    };
  }
  const ruleId =
    actionKey === 'cycle.pool.add' ? 'v2-cycle-pool-add-allowed' : 'v2-cycle-pool-remove-allowed';
  return {
    allowed: true,
    decision: 'allow',
    ruleId,
    reasons: [{ code: 'DEFAULT_ALLOW', data: {} }],
    capabilityPassed: true,
    engine: V2_ENGINE_PROPOSAL_CONFIG,
  };
}

// ============================================================================
// V2 Evaluator (delegate to v1; tag by action set)
// ============================================================================

/**
 * Evaluate authorization via v2.
 * - values.accept: v2-native prereq enforcement (no delegation); engine "v2-session".
 * - criteria.edit / criteria.create / criteria.delete: v2-native prereq enforcement (no delegation); engine "v2-proposal-config".
 * - Other governance → engine "v2-legitimacy", delegate to v1.
 * - Other session → engine "v2-session", delegate to v1.
 * - Candidate modification → engine "v2-candidate-mod", delegate to v1.
 * - Proposal configuration → engine "v2-proposal-config", delegate to v1.
 * - Else → engine "v2-shadow-delegate", delegate to v1.
 */
export function evaluateAuthorizationV2(
  input: AuthorizationEvaluationInput,
  delegates: AuthorizationV1Delegates
): AuthorizationDecision {
  const { action, capabilityChecker } = input;
  if (action.key === 'session.pool.add' || action.key === 'session.pool.remove') {
    if (isSessionPoolPrereqEnforceEnabled()) {
      return evaluateSessionPoolAction(
        input,
        capabilityChecker,
        action.key as 'session.pool.add' | 'session.pool.remove'
      );
    }
    const { actor, resource, context, rules } = input;
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  if (action.key === 'cycle.pool.add' || action.key === 'cycle.pool.remove') {
    if (isCyclePoolPrereqEnforceEnabled()) {
      return evaluateCyclePoolAction(
        input,
        capabilityChecker,
        action.key as 'cycle.pool.add' | 'cycle.pool.remove'
      );
    }
    const { actor, resource, context, rules } = input;
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  if (action.key === 'values.accept') {
    return evaluateValuesAccept(input, capabilityChecker);
  }
  if (ROUND_PROPOSAL_CRITERIA_ACTIONS.has(action.key)) {
    if (isRoundProposalPrereqEnforceEnabled()) {
      const roundDecision = evaluateRoundProposalAction(
        input,
        capabilityChecker,
        action.key as 'criteria.edit' | 'criteria.create' | 'criteria.delete'
      );
      if (!roundDecision.allowed) return roundDecision;
    }
    if (action.key === 'criteria.edit') return evaluateCriteriaEdit(input, capabilityChecker);
    if (action.key === 'criteria.create') return evaluateCriteriaCreate(input, capabilityChecker);
    if (action.key === 'criteria.delete') return evaluateCriteriaDelete(input, capabilityChecker);
  }
  if (isItemsWriteV2Enabled() && ITEMS_WRITE_ACTIONS.has(action.key)) {
    return evaluateItemsWriteAction(input, capabilityChecker, action.key, delegates);
  }
  if (action.key === 'lists.move_cutline' && isListsMoveCutlineV2Enabled()) {
    return evaluateListsMoveCutlineAction(input, capabilityChecker);
  }
  if (isProcessWriteV2Enabled() && PROCESS_WRITE_ACTIONS.has(action.key)) {
    return evaluateProcessWriteAction(input, capabilityChecker, action.key);
  }
  if (isSessionWriteV2Enabled() && SESSION_WRITE_ACTIONS.has(action.key)) {
    return evaluateSessionWriteAction(input, capabilityChecker, action.key);
  }
  if (isValuesWriteV2Enabled() && VALUES_WRITE_ACTIONS.has(action.key)) {
    return evaluateValuesWriteAction(input, capabilityChecker, action.key);
  }
  if (isPublishWriteV2Enabled() && PUBLISH_WRITE_ACTIONS.has(action.key)) {
    return evaluatePublishWriteAction(input, capabilityChecker, action.key);
  }
  // Workspace/tenant/platform admins allow within their scope (avoids NO_POLICY_MATCHED for list/process config etc.)
  const { actor, resource, context, rules } = input;
  const actorAny = actor as { currentWorkspaceId?: string };
  const resWorkspaceId = (resource as { workspaceId?: string; workspace_id?: string }).workspaceId ??
    (resource as { workspaceId?: string; workspace_id?: string }).workspace_id;
  const actorWorkspaceId = actorAny.currentWorkspaceId;
  const isAdmin = actor.roleIds.some((r: string) =>
    ['workspace-admin', 'tenant-admin', 'platform-admin'].includes(r)
  );
  const inScopeForAdmin =
    actor.roleIds.includes('platform-admin') ||
    actor.roleIds.includes('tenant-admin') ||
    (actor.roleIds.includes('workspace-admin') &&
      (resWorkspaceId == null || resWorkspaceId === actorWorkspaceId));
  const inScopeForRole = resWorkspaceId == null || resWorkspaceId === actorWorkspaceId;

  if (isAdmin && inScopeForAdmin) {
    return {
      allowed: true,
      decision: 'allow',
      ruleId: 'v2-admin-scope-allow',
      reasons: [{ code: 'ADMIN_IN_SCOPE', data: {} }],
      capabilityPassed: true,
      engine: getEngineForAction(action.key),
    };
  }
  // When a slice's flag is OFF, delegate to v1 (do not use generic capability-scope-allow for that action)
  if (!isItemsWriteV2Enabled() && ITEMS_WRITE_ACTIONS.has(action.key)) {
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  if (!isListsMoveCutlineV2Enabled() && action.key === 'lists.move_cutline') {
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  if (!isProcessWriteV2Enabled() && PROCESS_WRITE_ACTIONS.has(action.key)) {
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  if (!isSessionWriteV2Enabled() && SESSION_WRITE_ACTIONS.has(action.key)) {
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  if (!isValuesWriteV2Enabled() && VALUES_WRITE_ACTIONS.has(action.key)) {
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  if (!isPublishWriteV2Enabled() && PUBLISH_WRITE_ACTIONS.has(action.key)) {
    const decision = delegates.evaluateAuthorizationV1(
      actor,
      action,
      resource,
      context,
      rules,
      capabilityChecker
    );
    return { ...decision, engine: getEngineForAction(action.key) };
  }
  // Capability-only: allow if in scope and user has the capability for this action (e.g. SESSION_RUN for session.facilitate)
  if (inScopeForRole && capabilityChecker(action.key, resource.scope)) {
    return {
      allowed: true,
      decision: 'allow',
      ruleId: 'v2-capability-scope-allow',
      reasons: [{ code: 'CAPABILITY_IN_SCOPE', data: {} }],
      capabilityPassed: true,
      engine: getEngineForAction(action.key),
    };
  }
  const decision = delegates.evaluateAuthorizationV1(
    actor,
    action,
    resource,
    context,
    rules,
    capabilityChecker
  );
  if (decision.ruleId === 'default-deny' && decision.reasons[0]?.code === 'NO_POLICY_MATCHED') {
    const actionKey =
      typeof (input.action as any) === 'string'
        ? (input.action as any)
        : (input.action as any)?.key ?? null;
    console.warn('[AUTH NO_MATCH]', {
      action: actionKey,
      engineFromAction: getEngineForAction(action.key),
      actorRoleIds: actor.roleIds,
      actorUserId: actor.userId,
      contextPhase: (context as any)?.phase ?? null,
      resource: {
        id: (resource as any).id ?? null,
        workspaceId: (resource as any).workspaceId ?? (resource as any).workspace_id ?? null,
        createdByUserId: (resource as any).createdByUserId ?? (resource as any).created_by_user_id ?? null,
        ownerId: (resource as any).ownerId ?? null,
        assigneeId: (resource as any).assigneeId ?? (resource as any).shapingOwnerId ?? (resource as any).stewardId ?? null,
      },
      resourceDetailedState: (resource as any)?.exit_status ?? (resource as any)?.detailedState ?? (resource as any)?.detailed_state ?? null,
      resourceState: (resource as any)?.state ?? null,
    });
  }
  const engine = getEngineForAction(action.key);
  return { ...decision, engine };
}

/**
 * Evaluate and explain via v2; same engine tagging as evaluateAuthorizationV2.
 * values.accept and criteria.edit/create/delete use v2-native decision; explanation is derived from that decision.
 */
export function evaluateAndExplainV2(
  input: AuthorizationEvaluationInput,
  delegates: AuthorizationV1Delegates
): AuthorizationExplanation {
  const { action, capabilityChecker } = input;
  if (action.key === 'session.pool.add' || action.key === 'session.pool.remove') {
    if (isSessionPoolPrereqEnforceEnabled()) {
      const decision = evaluateSessionPoolAction(
        input,
        capabilityChecker,
        action.key as 'session.pool.add' | 'session.pool.remove'
      );
      const summaryKey = decision.allowed ? 'allowed' : 'denied';
      const summary =
        decision.reasons.length > 0
          ? decision.reasons.map((r) => r.code).join(', ')
          : summaryKey;
      return {
        allowed: decision.allowed,
        summaryKey,
        summary,
        details: { decision, reasons: decision.reasons },
        engine: decision.engine,
      };
    }
  }
  if (action.key === 'cycle.pool.add' || action.key === 'cycle.pool.remove') {
    if (isCyclePoolPrereqEnforceEnabled()) {
      const decision = evaluateCyclePoolAction(
        input,
        capabilityChecker,
        action.key as 'cycle.pool.add' | 'cycle.pool.remove'
      );
      const summaryKey = decision.allowed ? 'allowed' : 'denied';
      const summary =
        decision.reasons.length > 0
          ? decision.reasons.map((r) => r.code).join(', ')
          : summaryKey;
      return {
        allowed: decision.allowed,
        summaryKey,
        summary,
        details: { decision, reasons: decision.reasons },
        engine: decision.engine,
      };
    }
  }
  if (action.key === 'values.accept') {
    const decision = evaluateValuesAccept(input, capabilityChecker);
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  if (ROUND_PROPOSAL_CRITERIA_ACTIONS.has(action.key)) {
    let decision: AuthorizationDecision;
    if (isRoundProposalPrereqEnforceEnabled()) {
      const roundDecision = evaluateRoundProposalAction(
        input,
        capabilityChecker,
        action.key as 'criteria.edit' | 'criteria.create' | 'criteria.delete'
      );
      if (!roundDecision.allowed) {
        decision = roundDecision;
      } else {
        if (action.key === 'criteria.edit') decision = evaluateCriteriaEdit(input, capabilityChecker);
        else if (action.key === 'criteria.create') decision = evaluateCriteriaCreate(input, capabilityChecker);
        else decision = evaluateCriteriaDelete(input, capabilityChecker);
      }
    } else {
      if (action.key === 'criteria.edit') decision = evaluateCriteriaEdit(input, capabilityChecker);
      else if (action.key === 'criteria.create') decision = evaluateCriteriaCreate(input, capabilityChecker);
      else decision = evaluateCriteriaDelete(input, capabilityChecker);
    }
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  if (isItemsWriteV2Enabled() && ITEMS_WRITE_ACTIONS.has(action.key)) {
    const decision = evaluateItemsWriteAction(input, capabilityChecker, action.key, delegates);
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  if (action.key === 'lists.move_cutline' && isListsMoveCutlineV2Enabled()) {
    const decision = evaluateListsMoveCutlineAction(input, capabilityChecker);
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  if (isProcessWriteV2Enabled() && PROCESS_WRITE_ACTIONS.has(action.key)) {
    const decision = evaluateProcessWriteAction(input, capabilityChecker, action.key);
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  if (isSessionWriteV2Enabled() && SESSION_WRITE_ACTIONS.has(action.key)) {
    const decision = evaluateSessionWriteAction(input, capabilityChecker, action.key);
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  if (isValuesWriteV2Enabled() && VALUES_WRITE_ACTIONS.has(action.key)) {
    const decision = evaluateValuesWriteAction(input, capabilityChecker, action.key);
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  if (isPublishWriteV2Enabled() && PUBLISH_WRITE_ACTIONS.has(action.key)) {
    const decision = evaluatePublishWriteAction(input, capabilityChecker, action.key);
    const summaryKey = decision.allowed ? 'allowed' : 'denied';
    const summary =
      decision.reasons.length > 0
        ? decision.reasons.map((r) => r.code).join(', ')
        : summaryKey;
    return {
      allowed: decision.allowed,
      summaryKey,
      summary,
      details: { decision, reasons: decision.reasons },
      engine: decision.engine,
    };
  }
  const { actor, resource, context, rules } = input;
  const { explanation } = delegates.evaluateAndExplainV1(
    actor,
    action,
    resource,
    context,
    rules,
    capabilityChecker
  );
  const engine = getEngineForAction(action.key);
  return { ...explanation, engine };
}
